import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  X,
  AlertCircle
} from 'lucide-react';
import { Order, OrderStatus } from '../types/order';
import { AdminOrderService } from '../services/adminOrderService';
import useToastStore from '../store/useToastStore';

// Sub-components
import OrderHeader from '../components/orderDetails/OrderHeader';
import OrderInstructions from '../components/orderDetails/OrderInstructions';
import OrderItems from '../components/orderDetails/OrderItems';
import CustomerShippingInfo from '../components/orderDetails/CustomerShippingInfo';
import PaymentOrderSummary from '../components/orderDetails/PaymentOrderSummary';
import DeliveryInformation from '../components/orderDetails/DeliveryInformation';
import OrderActions from '../components/orderDetails/OrderActions';
import OrderModals from '../components/orderDetails/OrderModals';

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToastStore();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<() => void>(() => { });
  const [confirmationMessage, setConfirmationMessage] = useState<string>("");
  const [isCollecting, setIsCollecting] = useState(false);
  const [isEditingDelivery, setIsEditingDelivery] = useState(false);
  const [newDeliveryDate, setNewDeliveryDate] = useState<string>("");
  const [newTrackingId, setNewTrackingId] = useState<string>("");
  const [newTrackingUrl, setNewTrackingUrl] = useState<string>("");

  useEffect(() => {
    let prevScrollRestoration: string | undefined;
    if ('scrollRestoration' in window.history) {
      prevScrollRestoration = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
    }
    // Use a timeout to ensure it runs after browser's restoration
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 20);
    return () => {
      if (prevScrollRestoration) {
        window.history.scrollRestoration = prevScrollRestoration as ScrollRestoration;
      }
    };
  }, []);

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  useEffect(() => {
    if (order) {
      if (order.expectedDeliveryDate) {
        setNewDeliveryDate(order.expectedDeliveryDate.split('T')[0]);
      }
      setNewTrackingId(order.trackingId || '');
      setNewTrackingUrl(order.trackingLink || '');
    }
  }, [order]);

  const handleBackNavigation = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/orders');
    }
  };

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const orderData = await AdminOrderService.getOrderById(orderId!);
      setOrder(orderData);
    } catch (err) {
      setError('Failed to load order details');
      console.error('Error fetching order:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: OrderStatus, shippingDetails?: any) => {
    if (!order) return;

    try {
      if (newStatus === 'SHIPPED' && shippingDetails) {
        await AdminOrderService.shipOrder(order._id, shippingDetails);
        const method = shippingDetails.shippingMethod === 'partner' ? 'with partner' : 'manually';
        showToast(`Order shipped ${method} successfully`, 'success');
      } else {
        await AdminOrderService.updateOrderStatus(order._id, newStatus, shippingDetails);
        showToast('Order status updated successfully', 'success');
      }
      fetchOrder();
    } catch (error) {
      showToast('Failed to update order status', 'error');
      console.error('Error updating order status:', error);
    }
  };

  const handleCollectRemaining = async () => {
    if (!order) return;
    try {
      setIsCollecting(true);
      await AdminOrderService.collectRemaining(order._id);
      showToast('Remaining payment marked as collected', 'success');
      fetchOrder();
    } catch (error) {
      showToast('Failed to mark payment as collected', 'error');
    } finally {
      setIsCollecting(false);
    }
  };

  const handleUpdateDeliveryInfo = async () => {
    if (!order) return;

    try {
      const updates: any = {
        trackingId: newTrackingId.trim() || undefined,
        trackingLink: newTrackingUrl.trim() || undefined,
      };

      if (newDeliveryDate) {
        updates.expectedDeliveryDate = new Date(newDeliveryDate).toISOString();
      } else if (newDeliveryDate === '' && order.expectedDeliveryDate) {
        updates.expectedDeliveryDate = null;
      }

      await AdminOrderService.updateDeliveryInfo(order._id, updates);
      showToast('Delivery information updated successfully', 'success');
      setIsEditingDelivery(false);
      fetchOrder(); // Refresh order data
    } catch (error) {
      showToast('Failed to update delivery information', 'error');
      console.error('Error updating delivery info:', error);
    }
  };

  const handleConfirmAction = (action: () => void, message: string) => {
    setConfirmationAction(() => action);
    setConfirmationMessage(message);
    setShowConfirmationModal(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard', 'success');
  };

  const getStatusBadge = (status: OrderStatus) => {
    const statusConfig = {
      NEW: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', label: 'New' },
      PENDING: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Pending' },
      CONFIRMED: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Confirmed' },
      SHIPPED: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', label: 'Shipped' },
      DELIVERED: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Delivered' },
      CANCELLED: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Cancelled' }
    };

    const config = statusConfig[status];
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${config.bg} ${config.text} ${config.border}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-500 mb-4">{error || 'The order you are looking for does not exist.'}</p>
          <button
            onClick={handleBackNavigation}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <OrderHeader
          order={order}
          handleBackNavigation={handleBackNavigation}
          copyToClipboard={copyToClipboard}
          getStatusBadge={getStatusBadge}
        />

        <OrderInstructions instruction={order.orderInstruction} />

        <div className="space-y-8">
          <OrderItems items={order.items} />

          <CustomerShippingInfo
            shippingAddress={order.shippingAddress}
            copyToClipboard={copyToClipboard}
          />

          <PaymentOrderSummary
            order={order}
            onCollectRemaining={handleCollectRemaining}
            isCollecting={isCollecting}
          />


          <DeliveryInformation
            order={order}
            isEditingDelivery={isEditingDelivery}
            setIsEditingDelivery={setIsEditingDelivery}
            newDeliveryDate={newDeliveryDate}
            setNewDeliveryDate={setNewDeliveryDate}
            newTrackingId={newTrackingId}
            setNewTrackingId={setNewTrackingId}
            newTrackingUrl={newTrackingUrl}
            setNewTrackingUrl={setNewTrackingUrl}
            handleUpdateDeliveryInfo={handleUpdateDeliveryInfo}
            copyToClipboard={copyToClipboard}
          />

          <OrderActions
            order={order}
            handleConfirmAction={handleConfirmAction}
            handleStatusUpdate={handleStatusUpdate}
            setShowShippingModal={setShowShippingModal}
          />
        </div>
      </div>

      <OrderModals
        showShippingModal={showShippingModal}
        setShowShippingModal={setShowShippingModal}
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        order={order}
        confirmationMessage={confirmationMessage}
        confirmationAction={confirmationAction}
        handleStatusUpdate={handleStatusUpdate}
      />
    </div>
  );
};

export default OrderDetail;
